<template>
    <div class="body_sales_analysis">
        <div v-if="isLoading" class="state_message">
            <p>Cargando análisis…</p>
        </div>

        <div v-else-if="error" class="state_message">
            <p class="error_text">{{ error }}</p>
        </div>

        <div v-else-if="analysis" class="analysis_grid">
            <HeadlineMonthCard class="cell cell--headline"
                :period-label="analysis.period_label"
                :headline="analysis.headline"
                :kpis="analysis.clients_kpis" />

            <ClientsRankingCard class="cell cell--ranking"
                v-model="clientsQueryModel"
                :clients="displayedClients"
                :highlighted-clients="highlightedClients"
                :active-filter="activeFilterLabel"
                @clear-filter="clearSelectedFilter"
                @client-click="openClientDetail" />

            <StoresDistributionCard v-if="combinedDistributions.length"
                class="cell cell--distribution"
                :distributions="combinedDistributions"
                :selected-category="selectedFilter"
                @select-category="onSelectCategory"
                @focus-category="onFocusCategory"
                @blur-category="onBlurCategory" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import HeadlineMonthCard from "./HeadlineMonthCard.vue";
import StoresDistributionCard from "./StoresDistributionCard.vue";
import ClientsRankingCard from "./ClientsRankingCard.vue";
import { clientDetailModalConfig } from "./salesDrawerModals";
import { obligationForType } from "@/utils/counterpartObligation";

export default {
    name: "BodySalesAnalysis",
    components: {
        HeadlineMonthCard,
        StoresDistributionCard,
        ClientsRankingCard,
    },
    // Componente orquestador del feature: lee del módulo salesAnalysis del
    // store y reparte slices a las cards presentacionales. No expone props
    // — el modal pasa `bodyProps: null` y la lectura es vía Vuex.
    data() {
        return {
            // Set de nombres de clientes asociados al atributo hovereado
            // en StoresDistributionCard. `null` = sin foco activo, ranking
            // se ve normal. Cuando hay foco, `ClientsRankingCard` resalta
            // los del set y atenúa el resto.
            highlightedClients: null,
            // Selección persistente (click en una cuenta/categoría) que FILTRA
            // el ranking a los clientes asociados. `{ category_id, value }` o
            // null. Independiente del hover (highlight transitorio).
            selectedFilter: null,
        };
    },
    computed: {
        ...mapState("salesAnalysis", {
            analysis: "data",
            isLoading: "isLoading",
            error: "error",
            clientsQuery: "clientsQuery",
            analysisMonth: "month",
        }),
        ...mapGetters("salesAnalysis", ["filteredClients"]),
        // Distribución sintética "por cuenta": el primer donut de la card de
        // distribución se arma desde headline.breakdown ({account, amount}).
        // El backend no manda `share` para el breakdown, así que lo computamos
        // client-side (amount / total). Null cuando no hay breakdown.
        accountDistribution() {
            const breakdown = this.analysis?.headline?.breakdown ?? [];
            if (!breakdown.length) return null;
            const total = breakdown.reduce((sum, b) => sum + b.amount, 0);
            return {
                category_id: "accounts",
                category_label: "Cuenta",
                items: breakdown.map((b) => ({
                    name: b.account,
                    amount: b.amount,
                    share: total ? b.amount / total : 0,
                })),
            };
        },
        // Lista que alimenta StoresDistributionCard: cuenta primero, luego las
        // distribuciones por categoría (tiendas/marcas) que devuelve el backend.
        // En modo cuentas/clientes sin categorías, queda sólo el donut de cuenta.
        combinedDistributions() {
            const stores = Array.isArray(this.analysis?.stores_distribution)
                ? this.analysis.stores_distribution.filter(
                    (d) => Array.isArray(d.items) && d.items.length > 0,
                )
                : [];
            return [this.accountDistribution, ...stores].filter(Boolean);
        },
        // Bridge entre el v-model presentational del ranking card y el store
        // global. El padre asume el acople a Vuex; la card se queda pura.
        clientsQueryModel: {
            get() { return this.clientsQuery; },
            set(value) { this.setClientsQuery(value); },
        },
        // Ranking mostrado: con un filtro activo (click en cuenta/categoría) se
        // recorta a los clientes asociados; sin filtro, la lista completa
        // (ya filtrada por el buscador en el getter). Componible con el search.
        displayedClients() {
            if (!this.selectedFilter) return this.filteredClients;
            const { category_id, value } = this.selectedFilter;
            return this.filteredClients.filter(
                (c) => this.clientMatchesCategory(c, category_id, value),
            );
        },
        // Label del filtro activo para el chip del ranking ("Filtrando: X").
        activeFilterLabel() {
            return this.selectedFilter ? this.selectedFilter.value : "";
        },
    },
    watch: {
        // Cambió el mes analizado (refetch): el filtro/hover quedan obsoletos
        // (su valor puede no existir en la data nueva) → reset.
        analysisMonth() {
            this.selectedFilter = null;
            this.highlightedClients = null;
        },
    },
    methods: {
        ...mapMutations("salesAnalysis", { setClientsQuery: "SET_CLIENTS_QUERY" }),
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),

        // ¿El cliente está asociado a `value` en la categoría `category_id`?
        // Cada cliente trae su `categories` map del backend (incluye 'accounts'
        // para las cuentas). Compartido por el highlight (hover) y el filtro (click).
        clientMatchesCategory(client, category_id, value) {
            const values = client.categories && client.categories[category_id];
            return Array.isArray(values) && values.includes(value);
        },

        // Highlight cruzado (hover): el StoresDistributionCard emite
        // `focus-category` con el atributo hovereado; armamos el Set de clientes
        // asociados y lo pasamos a ClientsRankingCard para resaltarlos.
        onFocusCategory({ category_id, value }) {
            const clients = this.analysis?.clients_ranking || [];
            const matchSet = new Set();
            for (const c of clients) {
                if (this.clientMatchesCategory(c, category_id, value)) {
                    matchSet.add(c.name);
                }
            }
            this.highlightedClients = matchSet;
        },
        onBlurCategory() {
            this.highlightedClients = null;
        },

        // Click en una cuenta/categoría: filtra el ranking a sus clientes.
        // Toggle — reclickear el mismo item limpia el filtro.
        onSelectCategory({ category_id, value }) {
            const isSame = this.selectedFilter
                && this.selectedFilter.category_id === category_id
                && this.selectedFilter.value === value;
            this.selectedFilter = isSame ? null : { category_id, value };
        },
        clearSelectedFilter() {
            this.selectedFilter = null;
        },

        // El ranking emite `{ name, amount, share }`. Resolvemos el ci_id
        // dentro de la action (`openClientDetail` con alias) y abrimos el
        // segundo drawer encima del modal de análisis — Buefy soporta stack
        // de modales y propaga foco al último abierto.
        openClientDetail(client) {
            if (!client || !client.name || !this.analysisMonth) return;

            // El ranking sólo emerge desde el tab Ventas — el lado es siempre
            // por cobrar (document_type='C'). Cuando otros tabs (Gastos:
            // Proveedor='P') usen este flow, el caller debería determinar el
            // documentType. Para esto centralizamos el valor en una línea acá.
            const documentType = "C";
            // El tipo lo trae el propio ranking: una venta puede ser de un
            // 'Cliente' (factura) o de una 'Nota de Venta' (nota de ingreso),
            // y de eso dependen la obligación de metadata y el filtro de docs.
            const counterpartType = client.counterpart_type || null;

            this.openClientDetailAction({
                alias: client.name,
                documentType,
                monthIso: this.analysisMonth,
                obligationName: obligationForType(counterpartType),
                counterpartType,
            });

            // Config estático en el helper compartido (mismo drawer que abre
            // VentasTab). Width 70% < el 85% del primer nivel: este drawer se
            // apila encima del análisis del mes, dejar ver una franja del modal
            // padre refuerza la jerarquía del drill.
            this.$buefy.modal.open({
                ...clientDetailModalConfig({ fallbackAlias: client.name, width: "70%" }),
                parent: this,
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
// Cadena de altura: el body absorbe lo que le da .modal_back_caa (flex:1)
// y propaga vía grid → cells → cards. El scroll vive DENTRO de cada card,
// nunca acá. `min-height: 0` en body+grid+cell es lo que permite que el
// constraint se respete a través de los layers.
.body_sales_analysis {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.state_message {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: var(--report-text-muted);
    font-size: 14px;
}

.error_text {
    color: var(--report-negative);
}

// Dos columnas 50/50: la izquierda apila la card de monto+KPIs (auto) y el
// ranking de clientes (resto); la derecha es una sola card de distribución
// que ocupa toda la columna vertical (3 donuts apilados con su detalle).
.analysis_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr;
    gap: 24px;
}

// Monto + pills + KPIs — fila superior izquierda, alto natural de su contenido.
.cell--headline {
    grid-column: 1;
    grid-row: 1;
}

// Ranking de clientes — bajo el headline, toma el alto restante de la columna.
.cell--ranking {
    grid-column: 1;
    grid-row: 2;
}

// Distribución — columna derecha completa (ambas filas).
.cell--distribution {
    grid-column: 2;
    grid-row: 1 / -1;
}

.cell {
    // Ambos min-* son requisito para que la cell pueda ser más corta que su
    // contenido natural — sin esto el grid pide la altura intrínseca y se
    // pierde el clamp.
    min-width: 0;
    min-height: 0;
}
</style>
